import {
  BulkAcceptChangeRequestResponseDto,
  GfFlyerItemDto,
  GfFlyerReviewChangeRequest,
  GfFlyerReviewComment,
  GfMultiImage,
  PresenceSyncEntryPayload,
  PublicReviewErrorDto,
} from "@swagger/typescript-fetch-goflyer";

export type ReviewPresenceEntry = PresenceSyncEntryPayload & {
  audienceKey?: string;
  targetType?: string;
  targetId?: string;
  lastSeen?: string;
};

export const presenceKey = (p: ReviewPresenceEntry): string =>
  p.audienceKey ||
  p.audienceId ||
  (p.displayName ? `name:${p.displayName}` : "anon");

export type ReviewChangeRequest = GfFlyerReviewChangeRequest & {
  submittedAt?: string;
  submittedBy?: string;
  submittedByAudienceKey?: string;
};

export type ReviewChangeRequestStatus = GfFlyerReviewChangeRequest.StatusEnum;

/** Error `code` values we map to a dedicated toast (OpenAPI + one admin-only string). */
export type ReviewErrorCode =
  | PublicReviewErrorDto.CodeEnum
  | "AUDIENCE_KEY_TAKEN";

export type ReviewComment = GfFlyerReviewComment;

export type BulkAcceptResult = BulkAcceptChangeRequestResponseDto;

export type HistoryTarget = Pick<
  GfFlyerReviewChangeRequest,
  "flyerItemId" | "fieldKey"
>;

/**
 * `GfFlyerItemDto` plus the fields the global API decorator injects at runtime
 * (`gfMultiImages` derived from `gfImages`) and `rank`, which the generated DTO
 * omits. Lets summary mapping read them without an `as unknown as` cast.
 */
export type FlyerItemWithExtras = GfFlyerItemDto & {
  gfMultiImages?: GfMultiImage[];
  rank?: number;
};

/** The accept-change-request endpoint returns the change request it accepted. */
export type AcceptCrResponse = ReviewChangeRequest;

/**
 * Normalize the loosely-typed accept-change-request response (the SDK types it
 * as `{}`/`Response`) to a typed change request, or `undefined` if the body is
 * not an object. Single documented boundary cast.
 */
export const toAcceptedCr = (res: unknown): AcceptCrResponse | undefined =>
  res && typeof res === "object" ? (res as AcceptCrResponse) : undefined;

/**
 * Coerce an API timestamp (ISO string, `Date`, or epoch number) to epoch
 * milliseconds. Returns 0 for missing/invalid values. Replaces the duplicated
 * `toTime`/`toMs` helpers across the feature.
 */
export const toEpochMs = (
  d: string | Date | number | null | undefined,
): number => {
  if (d === null || d === undefined || d === "") return 0;
  const ms =
    d instanceof Date
      ? d.getTime()
      : typeof d === "number"
        ? d
        : new Date(d).getTime();
  return Number.isNaN(ms) ? 0 : ms;
};

/**
 * Normalize a list endpoint result that may be either a bare `T[]` or a
 * `{ items?: T[] }` envelope. Replaces the repeated
 * `Array.isArray(res) ? ... : (res as { items? }).items` casts.
 */
export const extractList = <T>(res: unknown): T[] => {
  if (Array.isArray(res)) return res as T[];
  const items = (res as { items?: unknown } | null | undefined)?.items;
  return Array.isArray(items) ? (items as T[]) : [];
};
